import React, {useContext, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Outlet, useLocation} from 'react-router-dom';
import {NetworkContext} from './lib/context';
import AuthWall from '../../components/auth-wall';
import {useNavigate} from 'react-router';
import {HeaderContext} from '../../components/header/context';
import Header from '../../components/header';
import MyContent from '../../components/header/my-content/my-content';
import NetworkOptimizationErrorBoundary from './components/error-boundary';

const NetworkOptimizationViewPage: React.FC = observer(() => {
    const networkOptimizationStore = useContext(NetworkContext);
    const headerStore = useContext(HeaderContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [errorBoundaryKey, setErrorBoundaryKey] = useState(0);
    const [hasError, setHasError] = useState(false);

    const isNetworkOptimization = location.pathname.includes('/network-optimization');
    headerStore.useNewDesign = true;
    
    const VALID_PAGE_NAMES = [
        'network-analysis',
        'reports',
        'generate-report',
        'compare'
    ];

    const isInvalidPath = () => {
        const pathParts = location.pathname.split('/').filter(Boolean);

        // Check if path starts with network-optimization
        if (pathParts[0] !== 'network-optimization') return true;

        // Only allow /network-optimization/my-files or full pattern
        if (pathParts.length === 1) {
            return true; // Reject /network-optimization alone
        }

        // Allow /network-optimization/my-files
        if (pathParts.length === 2 && pathParts[1] !== 'my-files') {
            return true;
        }

        // Check for pattern: /network-optimization/:rosterId/:revisionId/:pageName
        if (pathParts.length === 4) {
            const rosterId = pathParts[1];
            const revisionId = pathParts[2];
            const pageName = pathParts[3];

            if (rosterId === 'null' || rosterId === 'NaN' || revisionId === 'null' || revisionId === 'NaN') {
                return true;
            }

            if (!VALID_PAGE_NAMES.includes(pageName)) {
                return true;
            }
        }

        return false;
    };
    useEffect(() => {
        document.title = 'Network Optimizer';
        if (isInvalidPath()) {
            navigate('/network-optimization/my-files');
        }
        if (networkOptimizationStore.navItems.length === 0) {
            networkOptimizationStore.loadHeaders();
        }

        headerStore.hideExtraButtons = isNetworkOptimization;
    }, [navigate, location.pathname, headerStore, isNetworkOptimization]);

    useEffect(() => {
        // Reset error boundary on navigation only if there's an error
        if (hasError) {
            setErrorBoundaryKey(prev => prev + 1);
            setHasError(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const headerNavItems = networkOptimizationStore.navItems.filter(item => !item.hideTab).map((item) => ({label: item.label, path: `${item.useRosterID ? `${networkOptimizationStore.runId}/${networkOptimizationStore.revisionId}/` : ''}${item.path}`, hideTab: item.hideTab}));

    return (
        <AuthWall>
            <NetworkContext.Provider value={networkOptimizationStore}>
                <div className="network-optimization-page">
                    <header className="header">
                        <Header logoLink="/network-optimization/my-files" siteNameOverride="Network Optimization" showLogo={false} showOrg navItems={headerNavItems} hideProfileLinks/>
                    </header>
                    <NetworkOptimizationErrorBoundary key={errorBoundaryKey} onError={setHasError}>
                        <Outlet />
                    </NetworkOptimizationErrorBoundary>
                    {headerStore.showMyContent && <div className="fixed right-0 top-0 h-screen w-1/5 z-[100]">
                        <MyContent />
                    </div>}
                </div>
            </NetworkContext.Provider>
        </AuthWall>
    );
});
export default NetworkOptimizationViewPage;